import React from "react";
import { Truck, Globe2, Satellite, MapPin, Power, LayoutDashboard, FileText, Bell } from "lucide-react";

const ICON_MAP = { Truck, Globe2, Satellite, MapPin, Power, LayoutDashboard, FileText, Bell };

// Per-service visual signature for the feature illustration
const VISUALS = {
  "gestion-flotillas": { icon: "Truck", accent: "#00F0FF", scene: "fleet" },
  "amplia-cobertura": { icon: "Globe2", accent: "#00FF66", scene: "globe" },
  "rastreo-inteligente": { icon: "Satellite", accent: "#00F0FF", scene: "satellite" },
  "posicion-tiempo-real": { icon: "MapPin", accent: "#00F0FF", scene: "map" },
  "control-remoto": { icon: "Power", accent: "#FF3366", scene: "control" },
  "dashboard": { icon: "LayoutDashboard", accent: "#A78BFA", scene: "dashboard" },
  "reportes": { icon: "FileText", accent: "#00FF66", scene: "report" },
  "alertas": { icon: "Bell", accent: "#FFB020", scene: "alert" },
};

function FleetScene({ accent }) {
  return (
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="road" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={accent} stopOpacity="0" />
          <stop offset="50%" stopColor={accent} stopOpacity="0.8" />
          <stop offset="100%" stopColor={accent} stopOpacity="0" />
        </linearGradient>
      </defs>
      {[80, 160, 240, 320].map((y, i) => (
        <path key={i} d={`M0 ${y + i * 5} Q400 ${y - 30 + i * 10} 800 ${y + 20 + i * 5}`}
          stroke="url(#road)" strokeWidth="1.5" fill="none" className="gps-path" />
      ))}
      {[[150, 200],[320, 240],[480, 200],[630, 250],[200, 320],[420, 350],[600, 320]].map(([x,y], i) => (
        <g key={i}>
          <rect x={x-12} y={y-6} width="24" height="12" rx="2" fill={accent} opacity="0.85" />
          <rect x={x-12} y={y-6} width="24" height="12" rx="2" fill="none" stroke="white" strokeOpacity="0.4" />
          <circle cx={x} cy={y} r="20" fill="none" stroke={accent} strokeOpacity="0.4">
            <animate attributeName="r" from="12" to="36" dur="2.6s" begin={`${i*0.25}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" from=".6" to="0" dur="2.6s" begin={`${i*0.25}s`} repeatCount="indefinite" />
          </circle>
        </g>
      ))}
    </svg>
  );
}

function GlobeScene({ accent }) {
  return (
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(400 225)">
        {[120, 100, 80, 60, 40].map((r, i) => (
          <ellipse key={i} cx="0" cy="0" rx={r * 1.5} ry={r * 0.55} fill="none" stroke={accent} strokeOpacity={0.2 - i*0.025} />
        ))}
        {[120, 100, 80, 60, 40].map((r, i) => (
          <ellipse key={`v${i}`} cx="0" cy="0" rx={r * 0.55} ry={r * 1.5} fill="none" stroke={accent} strokeOpacity={0.2 - i*0.025} />
        ))}
        <circle r="155" fill="none" stroke={accent} strokeWidth="1.5" strokeOpacity="0.5" />
        {[[110, -40], [-95, 60], [60, 110], [-130, -10], [40, -100], [-60, -80], [120, 50]].map(([x,y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3.5" fill={accent} />
            <circle cx={x} cy={y} r="9" fill="none" stroke={accent}>
              <animate attributeName="r" from="3.5" to="22" dur="2.4s" begin={`${i*0.2}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" from=".7" to="0" dur="2.4s" begin={`${i*0.2}s`} repeatCount="indefinite" />
            </circle>
          </g>
        ))}
      </g>
    </svg>
  );
}

function SatelliteScene({ accent }) {
  return (
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(400 225)">
        {[60, 90, 130, 180].map((r, i) => (
          <circle key={i} r={r} fill="none" stroke={accent} strokeOpacity={0.35 - i*0.06} strokeDasharray={i % 2 ? "4 8" : "0"} />
        ))}
      </g>
      {Array.from({length: 5}).map((_, i) => {
        const angle = (i * 72) * Math.PI / 180;
        const x = 400 + Math.cos(angle) * 160;
        const y = 225 + Math.sin(angle) * 110;
        return (
          <g key={i}>
            <line x1="400" y1="225" x2={x} y2={y} stroke={accent} strokeOpacity="0.4" strokeDasharray="3 4" />
            <circle cx={x} cy={y} r="6" fill={accent} />
            <circle cx={x} cy={y} r="10" fill="none" stroke={accent}>
              <animate attributeName="r" from="6" to="28" dur="2.2s" begin={`${i*0.3}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" from=".8" to="0" dur="2.2s" begin={`${i*0.3}s`} repeatCount="indefinite" />
            </circle>
          </g>
        );
      })}
      <circle cx="400" cy="225" r="14" fill="white" />
      <circle cx="400" cy="225" r="22" fill="none" stroke={accent} strokeWidth="2" />
    </svg>
  );
}

function MapScene({ accent }) {
  return (
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeOpacity="0.06" />
        </pattern>
      </defs>
      <rect width="800" height="450" fill="url(#grid)" />
      <path d="M50 380 Q200 250 360 300 T720 160" stroke={accent} strokeWidth="2.5" fill="none" className="gps-path" />
      <path d="M100 100 Q260 200 400 200 T740 330" stroke={accent} strokeOpacity=".5" strokeWidth="1.5" fill="none" className="gps-path" />
      {[[180, 320, 1], [320, 280, 0], [480, 230, 1], [620, 180, 0], [240, 130, 0], [560, 290, 0]].map(([x,y,big], i) => (
        <g key={i}>
          <path d={`M ${x} ${y} l -6 -10 a 7 7 0 1 1 12 0 z`} fill={accent} />
          <circle cx={x} cy={y - 12} r="2.5" fill="white" />
          {big && (<circle cx={x} cy={y} r="18" fill="none" stroke={accent}>
            <animate attributeName="r" from="6" to="38" dur="2.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" from=".6" to="0" dur="2.5s" repeatCount="indefinite" />
          </circle>)}
        </g>
      ))}
    </svg>
  );
}

function ControlScene({ accent }) {
  return (
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(400 225)">
        <circle r="110" fill="none" stroke={accent} strokeOpacity="0.25" strokeDasharray="4 6" />
        <circle r="80" fill="none" stroke={accent} strokeOpacity="0.45" />
        <circle r="60" fill="rgba(255, 51, 102, 0.18)" stroke={accent} strokeWidth="2" />
        <g stroke={accent} strokeWidth="3" strokeLinecap="round">
          <line x1="0" y1="-25" x2="0" y2="-3" />
        </g>
        <circle r="35" fill="none" stroke={accent} strokeWidth="3" strokeDasharray="120 30" transform="rotate(-90)" />
      </g>
      {[80, 720, 80, 720].map((x, i) => {
        const y = i < 2 ? 80 : 370;
        return <rect key={i} x={x-15} y={y-15} width="30" height="30" rx="4" fill="none" stroke={accent} strokeOpacity="0.5" />;
      })}
    </svg>
  );
}

function DashboardScene({ accent }) {
  return (
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(60 60)">
        <rect width="320" height="140" rx="10" fill="none" stroke={accent} strokeOpacity="0.4" />
        <polyline points="20,110 70,80 120,95 170,55 220,70 270,40 300,30" fill="none" stroke={accent} strokeWidth="2" />
        <rect width="320" height="140" y="170" rx="10" fill="none" stroke={accent} strokeOpacity="0.4" />
        {[40, 80, 120, 160, 200, 240, 280].map((x, i) => (
          <rect key={i} x={x} y={180 + (i % 3) * 30} width="28" height={100 - (i*8)} fill={accent} opacity={0.4 + (i * 0.05)} />
        ))}
      </g>
      <g transform="translate(420 60)">
        <circle cx="160" cy="160" r="100" fill="none" stroke={accent} strokeOpacity="0.4" strokeWidth="20" strokeDasharray="380 240" transform="rotate(-90 160 160)" />
        <circle cx="160" cy="160" r="100" fill="none" stroke={accent} strokeOpacity="0.8" strokeWidth="20" strokeDasharray="220 400" transform="rotate(-90 160 160)" />
      </g>
    </svg>
  );
}

function ReportScene({ accent }) {
  return (
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(100 60)">
        <rect width="280" height="340" rx="10" fill="none" stroke={accent} strokeOpacity="0.5" />
        {[40, 80, 120, 160, 200, 240, 280].map((y, i) => (
          <line key={i} x1="20" y1={y} x2={i % 2 ? 220 : 260} y2={y} stroke="white" strokeOpacity="0.25" strokeWidth="1.5" />
        ))}
      </g>
      <g transform="translate(420 80)">
        <rect width="280" height="300" rx="10" fill="none" stroke={accent} strokeOpacity="0.5" />
        <polyline points="20,200 60,160 100,180 140,120 180,140 220,80 260,100" fill="none" stroke={accent} strokeWidth="2.5" />
        <polyline points="20,220 60,200 100,210 140,180 180,200 220,170 260,180" fill="none" stroke={accent} strokeOpacity="0.5" strokeWidth="2" />
        {[20, 60, 100, 140, 180, 220, 260].map((x, i) => {
          const ys = [200, 160, 180, 120, 140, 80, 100];
          return <circle key={i} cx={x} cy={ys[i]} r="3.5" fill={accent} />;
        })}
      </g>
    </svg>
  );
}

function AlertScene({ accent }) {
  return (
    <svg viewBox="0 0 800 450" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
      <g transform="translate(400 225)">
        {[60, 110, 170, 240].map((r, i) => (
          <circle key={i} r={r} fill="none" stroke={accent} strokeOpacity={0.45 - i*0.08}>
            <animate attributeName="r" from={r * 0.7} to={r * 1.3} dur="3s" begin={`${i*0.4}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" from={0.45 - i*0.08} to="0" dur="3s" begin={`${i*0.4}s`} repeatCount="indefinite" />
          </circle>
        ))}
        <g transform="translate(-22 -28)">
          <path d="M22 0 C12 0 6 8 6 18 v18 L0 44 v6 h44 v-6 l-6 -8 V18 c0 -10 -6 -18 -16 -18 z" fill={accent} />
          <circle cx="22" cy="58" r="6" fill={accent} />
        </g>
      </g>
    </svg>
  );
}

const SCENES = {
  fleet: FleetScene,
  globe: GlobeScene,
  satellite: SatelliteScene,
  map: MapScene,
  control: ControlScene,
  dashboard: DashboardScene,
  report: ReportScene,
  alert: AlertScene,
};

export default function ServiceVisual({ slug, label }) {
  const v = VISUALS[slug] || VISUALS["gestion-flotillas"];
  const Icon = ICON_MAP[v.icon] || Satellite;
  const Scene = SCENES[v.scene] || FleetScene;
  return (
    <figure className="relative rounded-2xl overflow-hidden chrome-border group" data-testid="service-visual">
      <div className="relative w-full h-[280px] sm:h-[380px] md:h-[460px] bg-gradient-to-br from-[#0a1424] via-[#06080D] to-[#020408]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute -inset-20 bg-[radial-gradient(ellipse_at_center,var(--g)_0%,transparent_60%)]"
          style={{ "--g": `${v.accent}33` }} />
        <Scene accent={v.accent} />
        <div className="absolute inset-0 noise-overlay" />

        {/* big floating icon */}
        <div className="absolute right-6 top-6 md:right-10 md:top-10 w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center bg-black/50 backdrop-blur-md border border-white/15 float-slow"
          style={{ boxShadow: `0 0 40px ${v.accent}44, inset 0 0 0 1px ${v.accent}55` }}>
          <Icon className="w-7 h-7 md:w-9 md:h-9" style={{ color: v.accent }} />
        </div>

        {/* bottom caption */}
        <div className="absolute inset-x-0 bottom-0 p-5 sm:p-7 md:p-9 bg-gradient-to-t from-[#06080D] via-[#06080D]/55 to-transparent">
          <div className="flex items-center gap-3">
            <span className="pulse-dot" />
            <figcaption className="font-tech text-base sm:text-lg md:text-xl text-white max-w-2xl">{label}</figcaption>
          </div>
        </div>

        {/* corner accents */}
        <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 rounded-tl-md" style={{ borderColor: `${v.accent}b3` }} />
        <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 rounded-tr-md" style={{ borderColor: `${v.accent}b3` }} />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 rounded-bl-md" style={{ borderColor: `${v.accent}b3` }} />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 rounded-br-md" style={{ borderColor: `${v.accent}b3` }} />
      </div>
    </figure>
  );
}
